import Vue from "vue";
import VueRouter from "vue-router";
import store from "../store/index.js";
import { PUBLIC_NAV_PATHS } from "../utils/publicNav";

Vue.use(VueRouter);

// vue-router rejects the pending navigation promise when a guard redirects it
// (e.g. the forced Terms & Conditions redirect to /account); those are benign,
// so swallow navigation failures and only propagate real errors.
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
	if (onResolve || onReject) {
		return originalPush.call(this, location, onResolve, onReject);
	}
	return originalPush.call(this, location).catch((err) => {
		if (VueRouter.isNavigationFailure(err)) {
			return err;
		}
		return Promise.reject(err);
	});
};

// Scroll position when the current navigation started, read before the page changes.
let scrollYBeforeNavigation = 0;

// The public header collapses to its link strip once the logo row is scrolled away. If it is
// collapsed when the user picks a new page, keep it collapsed: land just below the logo row
// instead of at the very top, which would expand it again.
function collapsedHeaderOffset(from) {
	// Only when the previous page had the same header; a signed-in page's scroll position says nothing about it.
	if (!PUBLIC_NAV_PATHS.includes(from.path)) return 0;
	const logoRow = document.querySelector(".public-header-top");
	if (!logoRow) return 0;
	return scrollYBeforeNavigation >= logoRow.offsetHeight ? logoRow.offsetHeight : 0;
}

const routes = [
	{
		path: "/",
		redirect: "/login",
	},
	{
		path: "/login",
		name: "Login",
		component: () => import("@/views/Public/Login.vue"),
		meta: {
			guest: true,
			title: "Chemformation - Login",
		},
	},
	{
		path: "/contact",
		name: "Contact",
		component: () => import("@/views/Public/Contact.vue"),
		meta: {
			title: "Chemformation - Contact Us",
		},
	},
	{
		path: "/request",
		name: "RequestAccess",
		component: () => import("@/views/Public/Request.vue"),
		meta: {
			requiresAuth: false,
			guest: true,
			title: "Chemformation - Request Access",
		},
	},
	{
		path: "/supplier-partner",
		name: "SupplierPartner",
		component: () => import("@/views/Public/Supplier.vue"),
		meta: {
			guest: true,
			title: "Chemformation - Contact Us",
		},
	},
	{
		path: "/vendor-search",
		name: "VendorSearch",
		component: () => import("@/views/Public/VendorSearch.vue"),
		meta: {
			requiresAuth: false,
			guest: true,
			title: "Powered by Chemformation",
		},
	},
	{
		path: "/tutorial",
		name: "VideoDemo",
		component: () => import("@/views/Public/VideoDemo.vue"),
		meta: {
			guest: true,
			title: "Chemformation - Tutorial",
		},
	},
	{
		path: "/ai-assistants",
		name: "AiAssistants",
		component: () => import("@/views/Public/AiAssistants.vue"),
		meta: {
			guest: true,
			title: "Chemformation - AI Assistants",
		},
	},
	{
		path: "/forgot-password",
		name: "ForgotPassword",
		component: () => import("@/views/Public/ForgotPassword.vue"),
		meta: {
			requiresAuth: false,
			guest: true,
			title: "Chemformation - Reset Password",
		},
	},
	{
		path: "/reset-password",
		name: "ResetPassword",
		component: () => import("@/views/Public/ResetPassword.vue"),
		meta: {
			requiresAuth: false,
			guest: true,
			title: "Chemformation - Reset Password",
		},
	},
	{
		path: "/product-search",
		name: "ProductSearch",
		component: () => import("@/views/Private/ProductSearch.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - Product Search",
		},
	},
	{
		path: "/internet-search",
		name: "InternetSearch",
		component: () => import("@/views/Private/InternetSearch.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - Internet Search",
		},
	},
	{
		path: "/formulations",
		name: "Formulations",
		component: () => import("@/views/Private/Formulations.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - formulation Search",
		},
	},
	{
		path: "/faq",
		name: "FAQ",
		component: () => import("@/views/Private/FAQ.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - FAQ Documents",
		},
	},
	{
		path: "/glossary",
		name: "Glossary",
		component: () => import("@/views/Private/Glossary.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - Glossary",
		},
	},
	{
		path: "/account",
		name: "Account",
		component: () => import("@/views/Private/Account.vue"),
		meta: {
			title: "Chemformation - Account Information",
		},
	},
	// Reports have been moved to the admin app
	{
		path: "/mail",
		name: "Mail",
		component: () => import("@/views/Private/Mail.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - Mail Messages",
		},
	},
	{
		path: "/mail-message",
		name: "MailMessage",
		component: () => import("@/views/Private/MailMessage.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - Mail Messages",
		},
	},
	{
		path: `/cross-reference`,
		name: "Cross Reference Preview",
		component: () => import("@/views/Private/CrossReference.vue"),
		meta: {
			requiresAuth: true,
			title: "Chemformation - Cross References",
		},
	},
];

const router = new VueRouter({
	mode: "history",
	base: process.env.BASE_URL,
	routes,
	// Start each new page at the top (keeping the public header collapsed if it already was);
	// restore the saved position on back and forward, and scroll to the anchor when the link
	// carries one. Section targets set scroll-margin-top so they clear the pinned header.
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		}
		if (to.hash) {
			return { selector: to.hash };
		}
		return { x: 0, y: collapsedHeaderOffset(from) };
	},
});

//redirects user if trying to access a page that requires login
router.beforeEach(async (to, from, next) => {
	scrollYBeforeNavigation = window.scrollY || 0;

	// Special cases for password reset routes - always allow
	if (to.path === "/reset-password" || to.path === "/forgot-password") {
		next();
		return;
	}

	// For routes that require authentication
	if (to.matched.some((record) => record.meta.requiresAuth)) {
		const sessionResult = await store.dispatch("verifySession");

		// Check if this was a rate limited request but we should maintain the session
		// This includes both actual 429 responses and cached sessions during rate limiting
		if (sessionResult?.maintainSession || sessionResult?.cached) {
			// User is rate limited but was previously authenticated - allow navigation
			if (store.getters.isLoggedIn) {
				if (!store.getters.userInfo?.acceptedTerms) {
					next("/account");
				} else {
					next();
				}
			} else {
				// No valid session to maintain
				next("/login");
			}
			return;
		}

		// Normal session verification result
		if (store.getters.isLoggedIn) {
			if (!store.getters.userInfo.acceptedTerms) {
				next("/account");
			} else {
				next();
			}
			return;
		}

		next("/login");
	} else {
		next();
	}
});

router.afterEach((to) => {
	Vue.nextTick(() => {
		document.title = to.meta.title;
	});

	// refresh system config
	store.dispatch("getConfig").catch((error) => {
		console.log("Error during fetch system config.", error);
	});
});

export default router;
